"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";

export type CategoryItem = {
  key: string;
  label: string;
  /** When true the item is rendered indented as a child of the previous parent */
  isSubcategory?: boolean;
};

interface Props {
  categories: CategoryItem[];
  selectedCategory: string;
  setSelectedCategory: (k: string) => void;
}

export default function CategorySelector({
  categories,
  selectedCategory,
  setSelectedCategory,
}: Props) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selected = categories.find((c) => c.key === selectedCategory);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="w-full">
      <label className="sr-only">Catégories</label>

      <div ref={dropdownRef} className="relative inline-block w-full md:w-auto">
        {/* Trigger */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full md:w-84 bg-white border border-[#EFEFEF] rounded-full px-4 py-3 text-[#1B365E] font-bold flex items-center justify-between"
        >
          <span className="truncate text-left">{selected?.label ?? "Sélectionner…"}</span>
          <svg
            width="14"
            height="8"
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`ml-2 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          >
            <path
              d="M1 1L7 7L13 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Dropdown */}
        {open && (
          <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-lg border border-[#EFEFEF] max-h-75 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden z-50 min-w-full md:min-w-70">
            {categories.map((category, index) => {
              const isSelected = selectedCategory === category.key;
              const isFirst = index === 0;
              const isLast = index === categories.length - 1;

              return (
                <button
                  key={category.key}
                  type="button"
                  onClick={() => {
                    setSelectedCategory(category.key);
                    setOpen(false);
                  }}
                  className={`
                    w-full cursor-pointer text-left text-sm transition-colors whitespace-nowrap
                    ${category.isSubcategory ? "pl-8 pr-4 py-2.5" : "px-4 py-3 font-semibold"}
                    ${isSelected
                      ? "bg-[#1B365E] text-white hover:bg-[#1B365E]"
                      : "text-[#1B365E] hover:bg-[#F5F5F5]"
                    }
                    ${isFirst ? "rounded-t-xl" : ""}
                    ${isLast ? "rounded-b-xl" : ""}
                  `}
                >
                  {category.isSubcategory && (
                    <span className="inline-flex items-center absolute left-2">
                      <Image
                      src="/assets/icons/corner-down-right.png"
                      alt=""
                      width={18}
                      height={18}
                      className={`mr-1.5 ${isSelected ? "opacity-100" : "opacity-70"}`}
                    />
                    </span>
                  )}
                  {category.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
